import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  LayoutDashboard, Building2, Users, MessageSquare, CreditCard, 
  TrendingUp, RefreshCw, Eye, Send, ChevronRight, Home, Loader2, Globe, Phone, Calendar, FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import Header from "../components/Header";
import CrawlerPanel from "../components/CrawlerPanel";
import BlogAdminPanel from "../components/admin/BlogAdminPanel";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || "https://imofit.ro";

// Generate WhatsApp Click to Chat link with claim token
const generateWhatsAppLink = (phone, listing, claimToken = null) => {
  // Clean phone number - remove spaces, keep only digits and +
  let cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  
  // Ensure it starts with country code
  if (cleanPhone.startsWith('07')) {
    cleanPhone = '40' + cleanPhone.substring(1);
  } else if (cleanPhone.startsWith('+')) {
    cleanPhone = cleanPhone.substring(1);
  }
  
  // Create personalized message with claim link
  const claimUrl = claimToken 
    ? `${SITE_URL}/claim/${claimToken}` 
    : `${SITE_URL}/revendica/${listing.id}`;
  
  const message = `Bună! 👋

Am găsit anunțul tău "${listing.title}" și te invit să îl publici GRATUIT pe platforma Imofit.

✅ Ajungi la cumpărători eligibili pentru credit ipotecar
✅ Simulator de credit integrat - clienții văd rata lunară instant
✅ Fără comisioane, direct de la proprietar

👉 Revendică anunțul aici:
${claimUrl}

Echipa Imofit 🏠`;

  // Encode message for URL
  const encodedMessage = encodeURIComponent(message);
  
  return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
};

const AdminPanel = () => {
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [viewings, setViewings] = useState([]);
  const [whatsappLogs, setWhatsappLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [crawling, setCrawling] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, listingsRes, leadsRes, viewingsRes, logsRes, usersRes, simulationsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/listings/all`),
        axios.get(`${API}/credit/leads`),
        axios.get(`${API}/viewing-requests`).catch(() => ({ data: [] })),
        axios.get(`${API}/whatsapp/logs`),
        axios.get(`${API}/admin/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/credit-simulations`).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setListings(listingsRes.data);
      setLeads(leadsRes.data);
      setViewings(viewingsRes.data || []);
      setWhatsappLogs(logsRes.data);
      setUsers(usersRes.data || []);
      setSimulations(simulationsRes.data || []);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  };

  const runCrawler = async () => {
    setCrawling(true);
    try {
      const response = await axios.post(`${API}/crawler/simulate`);
      toast.success(`Crawler simulat: ${response.data.created} anunțuri noi detectate`);
      fetchData();
    } catch (error) {
      toast.error("Eroare la rularea crawler-ului");
    } finally {
      setCrawling(false);
    }
  };

  const sendWhatsAppInvite = async (listingId, phone) => {
    try {
      await axios.post(`${API}/whatsapp/send?listing_id=${listingId}&phone=${encodeURIComponent(phone)}`);
      toast.success("Invitație WhatsApp trimisă (simulare)");
      fetchData();
    } catch (error) {
      toast.error("Eroare la trimiterea invitației");
    }
  };

  const handleWhatsAppWithClaim = async (listing) => {
    try {
      // Generate claim token
      const response = await axios.post(`${API}/listings/${listing.id}/generate-claim-token`);
      const token = response.data.token;
      
      // Generate WhatsApp link with token
      const whatsappUrl = generateWhatsAppLink(listing.phone, listing, token);
      
      // Log the invitation
      await axios.post(`${API}/whatsapp/send?listing_id=${listing.id}&phone=${encodeURIComponent(listing.phone)}`);
      
      // Open WhatsApp
      window.open(whatsappUrl, '_blank');
      toast.success("Link de claim generat! Se deschide WhatsApp...");
      
      fetchData();
    } catch (error) {
      console.error("Error generating claim link:", error);
      toast.error(error.response?.data?.detail || "Eroare la generarea linkului");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      detected: "bg-amber-100 text-amber-800",
      invited: "bg-blue-100 text-blue-800",
      claimed: "bg-emerald-100 text-emerald-800",
      published: "bg-emerald-700 text-white"
    };
    return <Badge className={styles[status] || "bg-stone-100"}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-stone-900 flex items-center gap-3">
              <LayoutDashboard className="w-8 h-8 text-emerald-700" />
              Panou Administrare
            </h1>
            <p className="text-stone-500 mt-1">Gestionează anunțuri, leaduri și mesaje</p>
          </div>
          
          <Button
            onClick={runCrawler}
            disabled={crawling}
            data-testid="run-crawler-button"
            className="bg-amber-500 hover:bg-amber-600 rounded-full"
          >
            {crawling ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Simulează Crawler
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 card-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.listings.total}</p>
                  <p className="text-stone-500 text-sm">Anunțuri total</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 card-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.whatsapp_sent}</p>
                  <p className="text-stone-500 text-sm">Mesaje trimise</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 card-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.credit_leads.total}</p>
                  <p className="text-stone-500 text-sm">Leaduri credit</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 card-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-700" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.users}</p>
                  <p className="text-stone-500 text-sm">Utilizatori</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Conversion Stats */}
        {stats && (
          <div className="bg-white rounded-xl p-4 sm:p-6 card-shadow mb-8">
            <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Statistici conversie
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <p className="text-xl font-bold text-amber-800">{stats.listings.detected}</p>
                <p className="text-amber-600 text-sm">Detectate</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-xl font-bold text-blue-800">{stats.listings.invited}</p>
                <p className="text-blue-600 text-sm">Invitate</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg">
                <p className="text-xl font-bold text-emerald-800">{stats.listings.claimed}</p>
                <p className="text-emerald-600 text-sm">Revendicate</p>
              </div>
              <div className="text-center p-3 bg-emerald-100 rounded-lg">
                <p className="text-xl font-bold text-emerald-900">{stats.listings.published}</p>
                <p className="text-emerald-700 text-sm">Publicate</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="listings" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl flex-wrap">
            <TabsTrigger value="listings" className="rounded-lg">
              Anunțuri ({listings.length})
            </TabsTrigger>
            <TabsTrigger value="crawler" className="rounded-lg">
              <Globe className="w-4 h-4 mr-1" />
              Crawler
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg" data-testid="users-tab">
              <Users className="w-4 h-4 mr-1" />
              Utilizatori ({users.length})
            </TabsTrigger>
            <TabsTrigger value="simulations" className="rounded-lg" data-testid="simulations-tab">
              <CreditCard className="w-4 h-4 mr-1" />
              Simulări ({simulations.length})
            </TabsTrigger>
            <TabsTrigger value="viewings" className="rounded-lg">
              <Calendar className="w-4 h-4 mr-1" />
              Vizionări ({viewings.length})
            </TabsTrigger>
            <TabsTrigger value="leads" className="rounded-lg">
              Cereri credit ({leads.length})
            </TabsTrigger>
            <TabsTrigger value="blog" className="rounded-lg">
              <FileText className="w-4 h-4 mr-1" />
              Blog
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="rounded-lg">
              WhatsApp ({whatsappLogs.length})
            </TabsTrigger>
          </TabsList>

          {/* Listings Tab */}
          <TabsContent value="listings">
            <div className="bg-white rounded-xl card-shadow overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titlu</TableHead>
                    <TableHead>Preț</TableHead>
                    <TableHead>Telefon</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sursă</TableHead>
                    <TableHead>Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listings.map((listing) => (
                    <TableRow key={listing.id}>
                      <TableCell className="font-medium max-w-[200px]">
                        <div className="truncate">{listing.title}</div>
                        <div className="text-xs text-stone-400">{listing.zone || listing.city}</div>
                      </TableCell>
                      <TableCell className="font-semibold text-emerald-600">
                        {listing.price?.toLocaleString()} {listing.currency || '€'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {listing.phone || '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(listing.status)}</TableCell>
                      <TableCell className="uppercase text-xs text-stone-500">
                        {listing.source}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Link to={`/proprietate/${listing.id}`}>
                            <Button variant="ghost" size="sm" title="Vezi anunț">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          {listing.phone && listing.status !== 'claimed' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Invită pe WhatsApp (cu link de claim)"
                              onClick={() => handleWhatsAppWithClaim(listing)}
                            >
                              <Phone className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {listings.length === 0 && (
                <div className="text-center py-12">
                  <Home className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">Nu există anunțuri</p>
                  <p className="text-stone-400 text-sm">Rulează crawler-ul pentru a detecta anunțuri</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Crawler Tab */}
          <TabsContent value="crawler">
            <div className="bg-white rounded-xl card-shadow p-6">
              <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                Import anunțuri din multiple surse
              </h3>
              <CrawlerPanel />
            </div>
          </TabsContent>

          {/* Viewings Tab */}
          <TabsContent value="viewings">
            <div className="bg-white rounded-xl card-shadow overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vizitator</TableHead>
                    <TableHead>Telefon</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Proprietate</TableHead>
                    <TableHead>Mesaj</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewings.map((viewing) => {
                    const listing = listings.find(l => l.id === viewing.listing_id);
                    return (
                      <TableRow key={viewing.id}>
                        <TableCell className="font-medium">{viewing.visitor_name}</TableCell>
                        <TableCell>
                          <a href={`tel:${viewing.visitor_phone}`} className="text-blue-600 hover:underline">
                            {viewing.visitor_phone}
                          </a>
                        </TableCell>
                        <TableCell>{viewing.visitor_email || '-'}</TableCell>
                        <TableCell className="max-w-[150px] truncate">
                          {listing?.title || viewing.listing_id}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate text-sm text-stone-500">
                          {viewing.message || '-'}
                        </TableCell>
                        <TableCell className="text-stone-500 text-sm">
                          {new Date(viewing.created_at).toLocaleDateString('ro-RO')}
                        </TableCell>
                        <TableCell>
                          <a 
                            href={generateWhatsAppLink(viewing.visitor_phone, { title: listing?.title || 'proprietate', id: viewing.listing_id })}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700">
                              <Phone className="w-4 h-4" />
                            </Button>
                          </a>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              
              {viewings.length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">Nu există cereri de vizionare</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <div className="bg-white rounded-xl card-shadow overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nume</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Telefon</TableHead>
                    <TableHead>Venit</TableHead>
                    <TableHead>Avans</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="font-medium">{lead.name}</TableCell>
                      <TableCell>{lead.email}</TableCell>
                      <TableCell>{lead.phone}</TableCell>
                      <TableCell>{lead.monthly_income.toLocaleString()} €</TableCell>
                      <TableCell>{lead.down_payment.toLocaleString()} €</TableCell>
                      <TableCell>
                        <Badge variant={lead.status === "new" ? "default" : "secondary"}>
                          {lead.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-stone-500 text-sm">
                        {new Date(lead.created_at).toLocaleDateString('ro-RO')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {leads.length === 0 && (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">Nu există leaduri de credit</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <BlogAdminPanel />
          </TabsContent>

          {/* WhatsApp Tab */}
          <TabsContent value="whatsapp">
            <div className="bg-white rounded-xl card-shadow overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Telefon</TableHead>
                    <TableHead>Mesaj</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {whatsappLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-medium">{log.phone}</TableCell>
                      <TableCell className="max-w-[300px] truncate text-stone-500">
                        {log.message.substring(0, 100)}...
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                          {log.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-stone-500 text-sm">
                        {new Date(log.created_at).toLocaleDateString('ro-RO')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {whatsappLogs.length === 0 && (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">Nu există mesaje WhatsApp trimise</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl card-shadow overflow-hidden">
              <div className="p-4 border-b border-stone-100">
                <h3 className="font-semibold text-stone-900">Utilizatori înregistrați</h3>
                <p className="text-sm text-stone-500">Toți utilizatorii care și-au creat cont pe platformă</p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nume</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Telefon</TableHead>
                    <TableHead>Data înregistrării</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">{user.name || '-'}</TableCell>
                      <TableCell>
                        <a href={`mailto:${user.email}`} className="text-blue-600 hover:underline">
                          {user.email}
                        </a>
                      </TableCell>
                      <TableCell>
                        {user.phone ? (
                          <a href={`tel:${user.phone}`} className="text-blue-600 hover:underline flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {user.phone}
                          </a>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-stone-500 text-sm">
                        {new Date(user.created_at).toLocaleDateString('ro-RO', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {users.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">Nu există utilizatori înregistrați</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Simulations Tab */}
          <TabsContent value="simulations">
            <div className="bg-white rounded-xl card-shadow overflow-hidden">
              <div className="p-4 border-b border-stone-100">
                <h3 className="font-semibold text-stone-900">Simulări de credit</h3>
                <p className="text-sm text-stone-500">Toate simulările de credit efectuate de utilizatori - LEAD-uri potențiale</p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Utilizator</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Preț proprietate</TableHead>
                    <TableHead>Credit solicitat</TableHead>
                    <TableHead>Perioadă</TableHead>
                    <TableHead>Data</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {simulations.map((sim, index) => (
                    <TableRow key={index} data-testid={`simulation-row-${index}`}>
                      <TableCell className="font-medium">{sim.user_name || sim.user_email}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <a href={`mailto:${sim.user_email}`} className="text-blue-600 hover:underline block text-sm">
                            {sim.user_email}
                          </a>
                          {sim.user_phone && (
                            <a href={`tel:${sim.user_phone}`} className="text-blue-600 hover:underline flex items-center gap-1 text-sm">
                              <Phone className="w-3 h-3" />
                              {sim.user_phone}
                            </a>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold text-emerald-700">
                        {sim.property_price?.toLocaleString('ro-RO')} €
                      </TableCell>
                      <TableCell>
                        {(sim.property_price - sim.down_payment)?.toLocaleString('ro-RO')} €
                        <span className="text-stone-400 text-sm ml-1">
                          ({Math.round((sim.down_payment / sim.property_price) * 100)}% avans)
                        </span>
                      </TableCell>
                      <TableCell>{sim.loan_term_years} ani</TableCell>
                      <TableCell className="text-stone-500 text-sm">
                        {new Date(sim.created_at).toLocaleDateString('ro-RO', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {simulations.length === 0 && (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">Nu există simulări de credit înregistrate</p>
                  <p className="text-sm text-stone-400 mt-1">Simulările vor apărea când utilizatorii vor folosi calculatorul de credit</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPanel;
